/**
 * Bank identifier validators — client-side mirror of
 * `/app/backend/services/iban_validator.py`.
 *
 * The two implementations MUST stay in sync — a candidate seeing "IBAN
 * valide ✔" and then getting rejected by the server on submit would be
 * a UX bug. Any change here goes hand-in-hand with a change there.
 */

// Country-specific canonical IBAN lengths — matches the backend list.
const COUNTRY_LENGTHS = {
    FR: 27, BE: 16, DE: 22, ES: 24, IT: 27, NL: 18,
    PT: 25, LU: 20, CH: 21, GB: 22, IE: 22, AT: 20,
    MC: 27, PL: 28,
};

const IBAN_RE = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;
const BIC_RE = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/;

export function normalizeIban(value) {
    if (!value) return "";
    return String(value).replace(/\s+/g, "").toUpperCase();
}

export function normalizeBic(value) {
    if (!value) return "";
    return String(value).replace(/\s+/g, "").toUpperCase();
}

/**
 * Return true iff `raw` is a syntactically valid IBAN with a passing
 * mod-97 checksum (ISO 13616). BigInt is used because European IBANs
 * exceed Number.MAX_SAFE_INTEGER once letters are expanded to digits.
 */
export function isValidIban(raw) {
    const iban = normalizeIban(raw);
    if (iban.length < 15 || iban.length > 34) return false;
    if (!IBAN_RE.test(iban)) return false;

    const country = iban.slice(0, 2);
    const expected = COUNTRY_LENGTHS[country];
    if (expected !== undefined && iban.length !== expected) return false;

    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let numeric = "";
    for (const ch of rearranged) {
        numeric += /[A-Z]/.test(ch)
            ? String(ch.charCodeAt(0) - 55) // A=10, B=11, ..., Z=35
            : ch;
    }
    try {
        return BigInt(numeric) % 97n === 1n;
    } catch {
        return false;
    }
}

/**
 * Return true iff `raw` is a syntactically valid BIC/SWIFT code (ISO 9362).
 * 8 or 11 chars, positions 1-4 = bank (letters), 5-6 = country (letters),
 * 7-8 = location (alphanum), 9-11 = optional branch (alphanum).
 */
export function isValidBic(raw) {
    const bic = normalizeBic(raw);
    if (bic.length !== 8 && bic.length !== 11) return false;
    return BIC_RE.test(bic);
}
